import type {Mentionable} from '@github-ui/markdown-editor'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {useQuery} from '@github-ui/react-query'
import {useMemo} from 'react'

/**
 * Fetches mention suggestions for user status input.
 * @param enabled - Whether to execute the query
 * @param organization - Optional organization context to filter suggestions
 * @returns Query result with formatted mentionable suggestions
 */
export function useMentionsQuery(enabled: boolean, organization?: {globalRelayId?: string}) {
  const pathname = useMemo(
    () =>
      organization?.globalRelayId
        ? `/suggestions?mention_suggester=1&global_id=${organization.globalRelayId}`
        : '/autocomplete/user-suggestions',
    [organization],
  )

  const queryKey = useMemo(
    () =>
      organization?.globalRelayId
        ? ['autocomplete-user-mentions-for-status-dialog', organization.globalRelayId, pathname]
        : ['autocomplete-user-mentions-for-status-dialog', pathname],
    [organization, pathname],
  )

  return useQuery({
    queryKey,
    enabled,
    queryFn: () =>
      queryFnFetch<Array<{id: string; login: string; name?: string}>>({
        queryDeps: {pathname},
      }),
    select: (data): Mentionable[] =>
      data.map(({login, name}) => ({
        identifier: login,
        description: name ?? login,
      })),
  })
}
